import type { ReactNode } from "react";
import { useCallback, useDeferredValue, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Combobox, DropdownButton } from "@similarweb/ui-components";

import { ScrollDismissBoundary } from "../../scroll-dismiss/ScrollDismissBoundary";
import { StyledBaseDropdownButtonWrap } from "../../styles";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

type FormSelectComboboxValue = string | number;

const SELECT_ID = "form-select";

const defaultFormatValue = <V extends FormSelectComboboxValue>(value: V): string => String(value);

type FormSelectComboboxProps<V extends FormSelectComboboxValue> = {
    // Drives the data-automation-id, mirroring the legacy FormSelect contract.
    fieldName: string;
    value?: V;
    options: readonly V[];
    onChange: (value: V) => void;
    formatValue?: (value: V) => string;
    placeholder?: string;
    width?: number;
    isDisabled?: boolean;
    // Custom trigger. MUST return a single ref-forwarding DOM element (e.g. a styled.div):
    // Combobox.Trigger merges its press handlers + ref onto this child via PopoverTrigger `asChild`,
    // so a custom-component wrapper that drops the ref leaves the trigger inert.
    renderButton?: () => ReactNode;
    hasSearch?: boolean;
    searchPlaceHolder?: string;
    // Custom search predicate; defaults to a case-insensitive match on the formatted option text.
    filterItem?: (option: V, searchTerm: string) => boolean;
    onOpenChange?: (isOpen: boolean) => void;
    // Required when the combobox is rendered inside a modal so the popover stacks above the modal layer.
    isTopLayer?: boolean;
};

/**
 * Non-virtualized VALUE-based single-select Combobox for form selects whose options are primitive
 * values formatted via `formatValue` (the modern replacement for the legacy eager-rendering
 * `FormSelect`). Options mount only while the dropdown is open (Combobox unmounts its content when
 * closed), so a closed select costs ~no DOM. Single-select: picking an option fires `onChange` and
 * closes the popover; the consumer owns the value.
 *
 * For item-based option lists that own their own rendering (flags, checkboxes, counts) use
 * SelectCombobox instead.
 */
const FormSelectCombobox = <V extends FormSelectComboboxValue>({
    fieldName,
    value,
    options,
    onChange,
    formatValue = defaultFormatValue,
    placeholder = "",
    width,
    isDisabled = false,
    renderButton,
    hasSearch = false,
    searchPlaceHolder,
    filterItem,
    onOpenChange,
    isTopLayer = false,
}: FormSelectComboboxProps<V>) => {
    const { translate } = useTranslate();

    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const deferredSearchTerm = useDeferredValue(searchTerm);

    if (isDisabled && isOpen) {
        setIsOpen(false);
        setSearchTerm("");
    }

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (isDisabled && open) {
                return;
            }

            setIsOpen(open);
            if (!open) {
                setSearchTerm("");
            }
            onOpenChange?.(open);
        },
        [isDisabled, onOpenChange],
    );

    const filteredOptions = useMemo(() => {
        const term = deferredSearchTerm.trim().toLowerCase();
        if (!term) {
            return options;
        }

        const matches =
            filterItem ?? ((option: V, searchValue: string) => formatValue(option).toLowerCase().includes(searchValue));

        return options.filter((option) => matches(option, term));
    }, [options, deferredSearchTerm, filterItem, formatValue]);

    const handleSelect = useCallback(
        (option: V) => {
            onChange(option);
            setIsOpen(false);
        },
        [onChange],
    );

    const buttonText = is.undefined(value) ? placeholder : formatValue(value);

    return (
        <ScrollDismissBoundary
            isOpen={isOpen}
            onClose={() => handleOpenChange(false)}
        >
            <Combobox
                isOpen={isOpen}
                onOpenChange={handleOpenChange}
                placement="bottom-start"
                width={width ? `${width}px` : undefined}
                data-automation={`${SELECT_ID}-${fieldName}`}
                {...(isTopLayer ? { "data-react-aria-top-layer": "true" } : {})}
            >
                <Combobox.Trigger>
                    {renderButton ? (
                        renderButton()
                    ) : (
                        <StyledBaseDropdownButtonWrap disabled={isDisabled}>
                            <DropdownButton
                                disabled={isDisabled}
                                width={width}
                                hasValue={!is.undefined(value)}
                            >
                                {buttonText}
                            </DropdownButton>
                        </StyledBaseDropdownButtonWrap>
                    )}
                </Combobox.Trigger>

                {hasSearch ? (
                    <Combobox.SearchInput
                        value={searchTerm}
                        onChange={setSearchTerm}
                        placeholder={searchPlaceHolder}
                    />
                ) : null}

                <Combobox.Content
                    emptyState={<Combobox.OptionLabel>{translate("dropdown.no.results")}</Combobox.OptionLabel>}
                >
                    {filteredOptions.map((option) => {
                        const optionText = formatValue(option);

                        return (
                            <Combobox.Option
                                key={String(option)}
                                id={String(option)}
                                textValue={optionText}
                                onClick={() => handleSelect(option)}
                            >
                                {optionText}
                            </Combobox.Option>
                        );
                    })}
                </Combobox.Content>
            </Combobox>
        </ScrollDismissBoundary>
    );
};

export default FormSelectCombobox;
